import React from "react";
import { motion } from "framer-motion";
import EnhancedSpeedometerTrustfull from "components/SpeedometerTrustfull/EnhancedSpeedometerTrustfull";
import Tabs from "shared/ui/Tabs";
import TrustfullReasonList from "components/TrustfullReasonList/TrustfullReasonList";

type Props = {
  result: any | null;
  error: string | null;
  onRestart: () => void;
};

const stripTags = (s: string) => s.replace(/<[^>]+>/g, "");

export default function FraudStepResult({ result: res, error, onRestart }: Props) {
  const score = res?.analysis?.score ?? 0;
  const status = res?.analysis?.status ?? "OK";

  const goodReasons = (res?.analysis?.insights ?? []).map((t: string, i: any) => ({
    code: `good_${i}`,
    name: "",
    description: stripTags(t),
  }));
  const badReasons = (res?.analysis?.suspicions ?? []).map((t: any, i: any) => ({
    code: `bad_${i}`,
    name: "",
    description: stripTags(t),
  }));

  const tabs = [
    {
      label: "TOUS",
      notification: false,
      content: (
        <TrustfullReasonList
          goodReasons={goodReasons}
          badReasons={badReasons}
        />
      ),
    },
    {
      label: "COHÉRENTS",
      notification: false,
      content: <TrustfullReasonList goodReasons={goodReasons} />,
    },
    {
      label: "INCOHÉRENTS",
      notification: false,
      content: <TrustfullReasonList badReasons={badReasons} />,
    },
  ];

  const styles = {
    gridTop: { display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 } as React.CSSProperties,
    card: { background: "#fff", borderRadius: 12, boxShadow: "0 10px 24px rgba(15,23,42,.06)", padding: 16 } as React.CSSProperties,
    badge: (s: string) =>
      ({
        padding: "4px 8px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 700,
        background: s === "OK" ? "#dcfce7" : s === "A_VERIFIER" ? "#fef3c7" : "#fee2e2",
        color: s === "OK" ? "#166534" : s === "A_VERIFIER" ? "#92400e" : "#991b1b",
      } as React.CSSProperties),
    infoRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 } as React.CSSProperties,
  };

  const renderPhotos = () => {
    const items: string[] = [];
    const seen = new Set<string>();
    (res?.data?.photos ?? []).forEach((u: string) => {
      const key = (u || "").split("?")[0];
      if (!seen.has(key)) {
        seen.add(key);
        items.push(u);
      }
    });
    if (items.length === 0) return null;
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 10,
          marginTop: 12,
        }}
      >
        {items.slice(0, 9).map((p, i) => {
          if (i === 0) return null;
          return (
            <img
              key={i}
              src={p}
              alt={`photo-${i}`}
              style={{
                width: "100%",
                height: 110,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <motion.div
      className="step-card"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <h2>Résultat de l'analyse</h2>

      {error && (
        <div
          style={{
            color: "#b91c1c",
            textAlign: "center",
            marginBottom: 16,
            whiteSpace: "pre-wrap",
          }}
        >
          {error}
        </div>
      )}

      {res && (
        <>
          <div style={styles.gridTop}>
            {/* Résumé de l'annonce */}
            <div style={styles.card}>
              <h4 style={{ margin: 0, color: "#1f2937" }}>RÉSUMÉ DE L'ANNONCE</h4>

              <div style={{ marginTop: 10, ...styles.infoRow }}>
                <div>
                  <div className="bold text-sm">TITRE</div>
                  <div className="text-dark text-sm">
                    {res.data?.titre || "—"}
                  </div>
                </div>
                <div>
                  <div className="bold text-sm">COMPTE</div>
                  <div className="text-dark text-sm">
                    {typeof res.data?.is_pro === "boolean"
                      ? res.data?.is_pro
                        ? "Professionnel"
                        : "Particulier"
                      : "—"}
                  </div>
                </div>
                <div>
                  <div className="bold text-sm">PRIX</div>
                  <div className="text-dark text-sm">
                    {res.data?.prix != null ? `${res.data?.prix} €` : "—"}
                  </div>
                </div>
                <div>
                  <div className="bold text-sm">SURFACE</div>
                  <div className="text-dark text-sm">
                    {res.data?.surface != null ? `${res.data?.surface} m²` : "—"}
                  </div>
                </div>
                <div>
                  <div className="bold text-sm">PIÈCES</div>
                  <div className="text-dark text-sm">
                    {res.data?.pieces != null ? res.data?.pieces : "—"}
                  </div>
                </div>
                <div>
                  <div className="bold text-sm">LOCALISATION</div>
                  <div className="text-dark text-sm">
                    {res.data?.localisation || "—"}
                  </div>
                </div>
                <div>
                  <div className="bold text-sm">TYPE</div>
                  <div className="text-dark text-sm">
                    {res.data?.type_logement || "—"}
                  </div>
                </div>

                {res.meta && typeof res.meta.owner_ads_count === "number" && (
                  <div>
                    <div className="bold text-sm">ANNONCES DU VENDEUR</div>
                    <div className="text-dark text-sm">
                      {res.meta.owner_ads_count}
                    </div>
                  </div>
                )}

                {res.meta && (
                  <div>
                    <div className="bold text-sm">TÉL VÉRIFIÉ</div>
                    <div className="text-dark text-sm">
                      {res.meta.phone_verified === true ? (
                        <span className="tag tag-success">Oui</span>
                      ) : res.meta.phone_verified === false ? (
                        "Non"
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                )}

                {res.meta?.member_since_raw && (
                  <div>
                    <div className="bold text-sm">MEMBRE DEPUIS</div>
                    <div className="text-dark text-sm">
                      {res.meta.member_since_raw}
                    </div>
                  </div>
                )}
              </div>

              {res.meta && (
                <div
                  style={{
                    display: "flex",
                    gap: 18,
                    flexWrap: "wrap",
                    fontSize: 13,
                    color: "#475569",
                    marginTop: 8,
                  }}
                >
                  {res.meta.is_reactive === true && (
                    <div className="tag tag-info">Réactif</div>
                  )}
                  {res.meta.phone_verified === true && (
                    <div className="tag tag-info">Numéro vérifié</div>
                  )}
                  {res.meta.last_activity && (
                    <div>Dernière activité : {res.meta.last_activity}</div>
                  )}
                </div>
              )}

              {res.data?.description && (
                <div
                  style={{
                    marginTop: 10,
                    color: "#334155",
                    fontSize: 13,
                  }}
                >
                  {res.data?.description}
                </div>
              )}

              {renderPhotos()}
            </div>

            {/* Speedometer & prix */}
            <div style={styles.card}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: 8,
                }}
              >
                <span style={styles.badge(status)}>{status}</span>
              </div>

              <div>
                <EnhancedSpeedometerTrustfull
                  score={Math.round((score / 100) * 100)}
                  animate
                />
              </div>

              {(res.market?.eur_m2 || (res.market as any)?.band_min) && (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: 13,
                    color: "#334155",
                  }}
                >
                  {(() => {
                    const eurm2 =
                      res.market?.eur_m2 != null
                        ? res.market.eur_m2.toFixed(1)
                        : undefined;
                    const lo = (res.market as any)?.band_min;
                    const hi = (res.market as any)?.band_max;
                    if (eurm2 && lo != null && hi != null)
                      return (
                        <>
                          Prix annoncé : <b>{eurm2} €/m²</b> — Référence : {lo}-
                          {hi} €/m²
                        </>
                      );
                    if (eurm2)
                      return (
                        <>
                          Prix annoncé : <b>{eurm2} €/m²</b>
                        </>
                      );
                    return null;
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Résultat global */}
          <div style={{ marginTop: 12, ...styles.card }}>
            <div className="bold">RÉSULTAT DE L'ANALYSE</div>
            <div className="text-dark" style={{ marginTop: 6 }}>
              {score >= 80
                ? "Les informations transmises semblent cohérentes. Aucune alerte particulière n’a été détectée."
                : score >= 50
                  ? "Quelques points demandent vérification. Nous recommandons de vérifier les éléments listés ci-dessous."
                  : "Les informations analysées présentent des incohérences. ID Protect ne recommande pas d’engager une transaction sans vérifications approfondies."}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <Tabs tabs={tabs} />
          </div>
        </>
      )}

      <div style={{ marginTop: 20 }}>
        <button className="wizard-btn primary" onClick={onRestart}>
          Faire une nouvelle vérification
        </button>
      </div>
    </motion.div>
  );
}
